import { useState } from 'react'
import { converterComissao } from '../../importacao/validacao'

// O passo que pergunta QUEM governa a comissao desta tabela.
//
// Pedido dela em 26/08: "a comissao da fornecedora so vai valer se a tabela
// importada nao tiver o campo de comissao em nenhum produto, ai pode ter a
// pergunta 'tabela sem campo comissao deseja seguir regra fixa da
// fornecedora?'". Quando a tabela JA tem comissao propria, as palavras dela
// para a pergunta foram: "manter comissao da tabela anterior ou a politica de
// comissao da fornecedora?".
//
// ⚠ A opcao SEGURA vem sempre primeiro. Quando ha o que perder, o botao que
// perde e o de baixo, e ele diz o que se perde ANTES do toque -- nao depois.
export default function EscolhaPoliticaComissao({
  nomeTabela,
  nomeFornecedora,
  jaTemComissaoPropria,
  produtosDaTabela,
  pctDaFornecedora,
  podeDefinirPctDaFornecedora,
  aoSeguirFornecedora,
  aoDecidirNaTabela,
  enviando = false,
}: {
  nomeTabela: string
  nomeFornecedora: string
  jaTemComissaoPropria: boolean
  produtosDaTabela: number
  pctDaFornecedora: number | null
  podeDefinirPctDaFornecedora: boolean
  // `null` = a fornecedora ja tem % e nada precisa ser digitado.
  aoSeguirFornecedora: (pctDigitado: string | null) => void
  aoDecidirNaTabela: () => void
  enviando?: boolean
}) {
  const [pct, setPct] = useState('')
  const precisaDigitar = pctDaFornecedora === null && podeDefinirPctDaFornecedora
  const pctValido = converterComissao(pct.trim()) !== null

  const primario =
    'w-full rounded-lg py-3 font-medium bg-teal-700 text-white disabled:opacity-50'
  const secundario =
    'w-full rounded-lg py-3 font-medium border border-slate-300 text-slate-700 disabled:opacity-50'

  // O botao que faz a tabela seguir a fornecedora, com o rotulo certo para cada
  // caso. Fica numa variavel porque a ORDEM dos dois botoes muda: quando ha
  // comissao propria a perder, este desce para segundo.
  const botaoFornecedora = (
    <button
      type="button"
      disabled={enviando || (precisaDigitar && !pctValido) || !podeDefinirPctDaFornecedora}
      onClick={() => aoSeguirFornecedora(precisaDigitar ? pct.trim() : null)}
      className={jaTemComissaoPropria ? secundario : primario}
    >
      {jaTemComissaoPropria ? 'Passar a seguir ' : 'Seguir '}
      {nomeFornecedora}
      {pctDaFornecedora !== null && ` — ${pctDaFornecedora}%`}
      {precisaDigitar && pctValido && ` — ${pct.trim()}%`}
    </button>
  )

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <h3 className="font-medium text-slate-800">
        {jaTemComissaoPropria
          ? 'Esta planilha veio sem comissão'
          : 'Esta tabela veio sem comissão'}
      </h3>

      <p className="text-sm text-slate-600">
        {jaTemComissaoPropria ? (
          <>
            A tabela “{nomeTabela}” já tem comissão própria
            {produtosDaTabela > 0 && ` em ${produtosDaTabela} produto(s)`}. Você quer manter essa
            comissão, ou passar a seguir a política de {nomeFornecedora}?
          </>
        ) : pctDaFornecedora !== null ? (
          // ⚠ NAO dizer "seriam vendidos a 0%" aqui: com a fornecedora tendo um
          // % fixo, e ELE que valeria -- e e o que ja acontece hoje, calado.
          // Esta tela existe para tornar essa heranca visivel, nao para
          // inventar um perigo que nao existe.
          <>
            Nenhum produto desta planilha traz comissão. Sem escolher, os produtos de “{nomeTabela}”
            seguem a regra de {nomeFornecedora} — {pctDaFornecedora}%.
          </>
        ) : (
          <>
            Nenhum produto desta planilha traz comissão, e {nomeFornecedora} também não tem um % fixo.
            Sem definir, os produtos de “{nomeTabela}” seriam vendidos a 0% sem aviso.
          </>
        )}
      </p>

      {/* O campo so aparece quando ha o que preencher E preencher e seguro. */}
      {precisaDigitar && (
        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
          <label htmlFor="pct-fornecedora" className="block text-sm text-slate-700">
            {nomeFornecedora} ainda não tem um % fixo. Definir agora:
          </label>
          <div className="flex items-center gap-2">
            <input
              id="pct-fornecedora"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              inputMode="decimal"
              placeholder="ex.: 3"
              className="w-24 border border-slate-300 rounded-lg px-2 py-2 text-right"
            />
            <span className="text-slate-600">%</span>
          </div>
          <p className="text-xs text-slate-500">
            Vale para toda tabela de {nomeFornecedora} que não tiver comissão própria.
          </p>
        </div>
      )}

      {/* Quando a regra da fornecedora ja paga POR PRODUTO, gravar um % fixo
          por cima faria esses percentuais pararem de valer em silencio. O passo
          nao oferece, e diz por que -- caixa muda aqui seria pior do que a
          recusa. */}
      {pctDaFornecedora === null && !podeDefinirPctDaFornecedora && (
        <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
          {nomeFornecedora} paga comissão <strong>por produto</strong>, e não um % fixo. Trocar isso
          por um % fixo aqui faria os percentuais que ela já tem pararem de valer, sem aviso. Se for
          isso que você quer, mude na tela da Fornecedora — aqui só dá para definir produto a
          produto.
        </p>
      )}

      <div className="space-y-2 pt-1">
        {jaTemComissaoPropria ? (
          <>
            <button
              type="button"
              disabled={enviando}
              onClick={aoDecidirNaTabela}
              className={primario}
            >
              Manter a comissão desta tabela
            </button>
            {botaoFornecedora}
            {/* O aviso vem ANTES do toque, colado no botao que causa a perda. */}
            <p className="text-xs text-red-700 bg-red-50 rounded-lg p-2">
              ⚠ Ao seguir {nomeFornecedora}, a comissão própria de “{nomeTabela}”
              {produtosDaTabela > 0 && ` (${produtosDaTabela} produto(s))`} deixa de valer. Você não
              desfaz isso sozinha pelo app.
            </p>
          </>
        ) : (
          <>
            {botaoFornecedora}
            <button
              type="button"
              disabled={enviando}
              onClick={aoDecidirNaTabela}
              className={secundario}
            >
              Não, definir produto a produto
            </button>
          </>
        )}
      </div>
    </div>
  )
}
